import { Link, type LinkProps } from "@tanstack/react-router";
import type { MouseEvent } from "react";

import ActionButton from "@/components/Button/ActionButton";
import HStack from "@/components/Layout/HStack";
import { cn } from "@/lib/cn";
import { defaultNS, useTranslation } from "@/lib/i18n";

type QueryResult<
  Data extends {
    operationPackagePatternId: string;
    count: number;
  } = {
    operationPackagePatternId: string;
    count: number;
  },
> = {
  data: Data | undefined;
  isLoading: boolean;
  error: Error | null;
};

type TabItem = {
  id: string;
  label: string;
};

type Props = {
  tabs: TabItem[];
  onTabChange: (tab: string) => void;
  className?: string;
  onAddTab?: () => void;
  isAddingPattern?: boolean;
  activePatternId?: string;
  operationPackageId: string;
  to: Exclude<LinkProps["to"], undefined>;
  useGetOperationTaskCountInProgress: (arg: {
    operationPackageId: string;
    patternId: string;
  }) => QueryResult;
};

export function PatternTypeTabs({
  tabs,
  onTabChange,
  className,
  onAddTab,
  isAddingPattern,
  activePatternId,
  operationPackageId,
  to,
  useGetOperationTaskCountInProgress,
}: Props) {
  const { t } = useTranslation(defaultNS, {
    keyPrefix: "features.operationPackage",
  });

  return (
    <HStack
      className={cn(
        "items-center gap-2 overflow-x-auto border-b border-neutral-muted-on-fill",
        className,
      )}
    >
      <div className="relative flex px-1 pt-2">
        <div className="flex w-full overflow-hidden" role="tablist">
          <HStack className="min-w-max gap-5">
            {tabs.map((tab) => (
              <TabItem
                key={tab.id}
                tab={tab}
                isActive={tab.id === activePatternId}
                onClick={(e) => {
                  e.preventDefault();
                  onTabChange(tab.id);
                }}
                to={to}
                operationPackageId={operationPackageId}
                useGetOperationTaskCountInProgress={
                  useGetOperationTaskCountInProgress
                }
              />
            ))}
          </HStack>
        </div>
      </div>

      {/* NOTE:初期リリースではパターンのマイグレーションをしないため初回に追加するボタンを表示して操作する必要がある */}
      {onAddTab && tabs.length === 0 && (
        <ActionButton
          icon="PlusIcon"
          size="sm"
          variant="ghost"
          iconColor="primary"
          onClick={onAddTab}
          disabled={isAddingPattern}
        >
          {t("addPattern.addButtonLabel")}
        </ActionButton>
      )}
    </HStack>
  );
}

// 個別のタブアイテムをコンポーネント化して、各タブごとにクエリを実行
type TabItemProps = {
  tab: TabItem;
  isActive: boolean;
  onClick: (e: MouseEvent) => void;
  operationPackageId: string;
  to: Exclude<LinkProps["to"], undefined>;
  useGetOperationTaskCountInProgress: (arg: {
    operationPackageId: string;
    patternId: string;
  }) => QueryResult;
};

// ルーティングに基づいてTanstackRouterの機能でタブのアクディブを設定するのではなく、
// 状態(URL Search Params)に基づいてアクディブを制御するため、HeaderTabsを使用せず独自定義
function TabItem({
  tab,
  isActive,
  onClick,
  operationPackageId,
  to,
  useGetOperationTaskCountInProgress,
}: TabItemProps) {
  const { data: taskCount } = useGetOperationTaskCountInProgress({
    operationPackageId,
    patternId: tab.id,
  });

  const count = taskCount?.count ?? 0;

  return (
    <Link
      to={to}
      className={cn(
        "group relative whitespace-nowrap px-3 py-1.5",
        isActive && "font-semibold",
      )}
      role="tab"
      aria-selected={isActive}
      onClick={onClick}
    >
      <>
        <span>{tab.label}</span>
        {count > 0 && <span className="ml-1 text-xs">{count}</span>}
      </>
      {/* アクティブな場合に表示される下線 */}
      <span
        aria-hidden
        className={cn(
          "absolute bottom-0 left-0 h-1 w-full translate-y-1/2 rounded-full bg-primary",
          isActive ? "visible" : "invisible",
        )}
      />
    </Link>
  );
}
